"use client";

import React from "react";
import { Activity, Clock, Info, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface StepItem {
  number: number;
  title: string;
  description: string;
}
const vo2Steps: StepItem[] = [{
  number: 1,
  title: "Pre-Test Questionnaire",
  description: "Review relevant health, exercise and readiness information."
}, {
  number: 2,
  title: "Equipment Setup",
  description: "A heart-rate monitor and metabolic face mask are fitted."
}, {
  number: 3,
  title: "Progressive Exercise",
  description: "Exercise begins at an easier intensity and becomes progressively more demanding according to the selected protocol. Use a treadmill or cycle depending on the testing setup and individual requirements."
}, {
  number: 4,
  title: "Metabolic Measurement",
  description: "The CardioCoach measures respiratory gases throughout the assessment."
}, {
  number: 5,
  title: "Results",
  description: "Results are explained in practical language, including how they may inform training."
}];
const rmrSteps: StepItem[] = [{
  number: 1,
  title: "Preparation",
  description: "Confirm that appropriate pre-test preparation has been followed."
}, {
  number: 2,
  title: "Rest",
  description: "Allow the individual to settle into a comfortable resting state."
}, {
  number: 3,
  title: "Measurement",
  description: "Breathing is measured through the metabolic system while the person remains at rest."
}, {
  number: 4,
  title: "Results",
  description: "Explain measured resting energy expenditure and how it relates to overall energy requirements."
}];
export default function TestingWalkthrough() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return <section id="testingwalkthrough" data-nav="light" ref={ref} className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-12 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          {/* Sub-section A: VO2 Assessment */}
          <div className="flex flex-col h-full">
            <div className="mb-6">
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground">
                What happens during a VO₂ assessment?
              </h2>
            </div>

            <div className="flex-1 flex flex-col gap-4">
              {vo2Steps.map((step, i) => <Card key={step.number} data-index={i} className="border border-border bg-card shadow-xs transition-colors hover:border-primary/40">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-3">
                      <span className="flex size-7 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold text-xs shrink-0">
                        {step.number}
                      </span>
                      <CardTitle className="text-base font-medium text-foreground">
                        {step.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-sm text-muted-foreground leading-relaxed pl-10">
                      {step.description}
                    </p>
                  </CardContent>
                </Card>)}
            </div>

            <div className="mt-6 rounded-lg border border-border/80 bg-muted/40 p-4 text-xs text-muted-foreground leading-relaxed flex items-start gap-3">
              <Clock className="size-4 text-primary shrink-0 mt-0.5" aria-hidden="true" />
              <p>
                KORR describes the active VO₂ testing phase as commonly taking approximately 10–15
                minutes, although the full PhysAnalytica appointment is longer because preparation,
                progressive testing, recovery and results interpretation are also required.
              </p>
            </div>
          </div>

          {/* Sub-section B: RMR Assessment */}
          <div className="flex flex-col h-full">
            <div className="mb-6">
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground">
                What happens during an RMR assessment?
              </h2>
              <p className="text-sm md:text-base text-muted-foreground mt-2">
                RMR is measured while you are resting comfortably rather than exercising.
              </p>
            </div>

            <div className="flex-1 flex flex-col gap-4">
              {rmrSteps.map((step, i) => <Card key={step.number} data-index={i} className="border border-border bg-card shadow-xs transition-colors hover:border-primary/40">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-3">
                      <span className="flex size-7 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold text-xs shrink-0">
                        {step.number}
                      </span>
                      <CardTitle className="text-base font-medium text-foreground">
                        {step.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-sm text-muted-foreground leading-relaxed pl-10">
                      {step.description}
                    </p>
                  </CardContent>
                </Card>)}
            </div>

            <div className="mt-6 rounded-lg border border-border/80 bg-muted/40 p-4 text-xs text-muted-foreground leading-relaxed flex items-start gap-3">
              <Info className="size-4 text-primary shrink-0 mt-0.5" aria-hidden="true" />
              <p>
                Total daily energy needs are influenced by activity, exercise, food intake and other
                individual factors, and are not identical to RMR.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}